import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import i18n from "@/lib/i18n";
import { environment } from "@/config/environment";
import { useBoundStore, getStoreState } from "@/stores";
import { clear } from "@/shared/utils/commonUtils";
import { HttpError } from "@/shared/errors/HttpError";
import { setSessionExpiredReason } from "@/features/auth/components/SessionExpiredDialog";
import { buildSecurityHeaders, setRateLimitUntil } from "@/shared/security";
import { toast } from "sonner";
import { resetUser } from "@/shared/posthog/identify";

interface ApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

class ApiService {
  private axiosInstance: AxiosInstance;
  private baseURL: string = environment.API_URL;
  private apiVersion: string = environment.API_VERSION;

  constructor() {
    this.axiosInstance = this.createAxiosInstance();
  }

  private createAxiosInstance(): AxiosInstance {
    const instance = axios.create({
      baseURL: this.baseURL,
      headers: { "Content-Type": "application/json" },
      timeout: 30000, // 30 seconds
      withCredentials: true, // Enable cookies (session-based auth)
    });

    instance.interceptors.request.use(this.handleRequest);
    instance.interceptors.response.use(this.handleResponse, this.handleError);

    return instance;
  }

  private handleRequest = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    // Session-based auth uses cookies, no need for Authorization header
    // Cookies are automatically sent with withCredentials: true

    // FormData must not use the instance default application/json; let the runtime set
    // multipart/form-data with a boundary (otherwise backends parse JSON and miss the file).
    if (config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }

    // Inject current language into headers (Accept-Language + x-lang for backend)
    const currentLanguage = useBoundStore.getState().currentLanguage;
    if (currentLanguage) {
      config.headers.set('Accept-Language', currentLanguage);
      config.headers.set('x-lang', currentLanguage);
    }

    // Inject security headers (X-Device-Fingerprint)
    const securityHeaders = await buildSecurityHeaders();
    for (const [key, value] of Object.entries(securityHeaders)) {
      config.headers.set(key, value);
    }

    return config;
  };

  private handleResponse = (response: AxiosResponse): AxiosResponse => {
    return response;
  };

  private handleError = (error: any): Promise<never> => {
    // Check for connection errors (no response from server)
    const isConnectionError = !error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || error.message?.includes('ERR_CONNECTION_REFUSED'));

    const status = isConnectionError ? 0 : (error.response?.status || 500);
    const statusText = error.response?.statusText;
    const data = error.response?.data;

    // Handle 429 Too Many Requests - rate limit with Retry-After
    if (error.response?.status === 429) {
      const retryAfterHeader = error.response?.headers?.['retry-after'];
      const retryAfterSeconds = retryAfterHeader != null ? parseInt(String(retryAfterHeader), 10) : 900;
      const validSeconds = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds : 900;
      const minutes = Math.ceil(validSeconds / 60);
      setRateLimitUntil(error.config?.url ?? 'default', validSeconds);
      const message = i18n.t('toasts.tooManyAttempts', { ns: 'common', minutes });
      toast.error(message);
      return Promise.reject(
        new HttpError(message, 429, { statusText, data, response: error.response })
      );
    }

    // Extract error message (could be string or array)
    const errorMessage = isConnectionError
      ? i18n.t('toasts.networkError', { ns: 'common' })
      : error.response?.data?.message || error.message || i18n.t('toasts.genericError', { ns: 'common' });

    // Handle 401 Unauthorized - clear auth state and redirect to login
    if (error.response?.status === 401) {
      const isSessionCheck = error.config?.url?.includes('auth/session');
      // Skip reload for session check — AuthProvider handles null, no need for full reload
      if (isSessionCheck) {
        const state = getStoreState();
        state.clearAuth();
        resetUser();
        return Promise.reject(
          new HttpError(
            Array.isArray(errorMessage) ? errorMessage[0] : errorMessage,
            status,
            { statusText, data, response: error.response }
          )
        );
      }
      // Skip redirect for login page to avoid loops
      if (window.location.pathname !== '/') {
        // Token misuse: BE sets X-Reauth-Required when session used from different IP/device
        const reauthRequired = error.response?.headers?.['x-reauth-required'];
        const reason = reauthRequired === 'true' || reauthRequired === true ? 'security' : 'absolute';
        setSessionExpiredReason(reason);
        // Clear auth state using getStoreState (non-React access)
        const state = getStoreState();
        state.clearAuth();
        // Clear any local storage (though we're using cookies now)
        clear();
        window.location.href = "/";
      }
    }

    // Create HttpError with all metadata preserved
    const httpError = new HttpError(
      Array.isArray(errorMessage) ? errorMessage[0] : errorMessage,
      status,
      {
        statusText,
        data,
        response: error.response,
      }
    );

    return Promise.reject(httpError);
  };

  /**
   * Prepends API version to URL if not already present
   * Skips versioning for absolute URLs or URLs already containing version prefix
   */
  private getVersionedUrl(url: string): string {
    // Skip if absolute URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Skip if already versioned (e.g., /v1/users, /v2/users)
    if (/^\/v\d+\//i.test(url)) {
      return url;
    }
    // Prepend version
    return `/${this.apiVersion}${url.startsWith('/') ? '' : '/'}${url}`;
  }

  async request<T>(
    method: string,
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const versionedUrl = this.getVersionedUrl(url);
      const response = await this.axiosInstance.request<T>({
        method,
        url: versionedUrl,
        data,
        ...config,
      });

      return {
        data: response.data,
        status: response.status,
        message: response.statusText,
      };
    } catch (error) {
      throw error;
    }
  }

  // Helper methods for common HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>("get", url, undefined, config);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>("post", url, data, config);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>("put", url, data, config);
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>("patch", url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>("delete", url, undefined, config);
  }

  /**
   * GET request that returns a blob (e.g. PDF) and the filename from Content-Disposition.
   * Use for file downloads. Errors with a JSON body are parsed so the rejection message is readable.
   */
  async getBlob(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<{ blob: Blob; filename: string }> {
    const versionedUrl = this.getVersionedUrl(url);
    try {
      const response = await this.axiosInstance.request<Blob>({
        method: "get",
        url: versionedUrl,
        responseType: "blob",
        ...config,
      });
      const blob = response.data as Blob;
      const filename =
        this.parseFilenameFromContentDisposition(
          response.headers["content-disposition"]
        ) ?? "download.pdf";
      return { blob, filename };
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        const contentType = error.response?.headers?.["content-type"] ?? "";
        if (contentType.includes("application/json")) {
          const text = await (error.response.data as Blob).text();
          try {
            const body = JSON.parse(text);
            const message =
              (Array.isArray(body?.message) ? body.message[0] : body?.message) ??
              body?.error ??
              i18n.t('toasts.downloadFailed', { ns: 'common' });
            throw new HttpError(message, error.response.status, {
              data: body,
              response: error.response,
            });
          } catch (parseError) {
            if (parseError instanceof HttpError) throw parseError;
          }
        }
      }
      throw error;
    }
  }

  /**
   * POST request that returns a blob (e.g. PDF) and the filename from Content-Disposition.
   * Use for endpoints that validate and return a file (e.g. reprint letter).
   */
  async postBlob(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<{ blob: Blob; filename: string }> {
    const versionedUrl = this.getVersionedUrl(url);
    try {
      const response = await this.axiosInstance.request<Blob>({
        method: "post",
        url: versionedUrl,
        data,
        responseType: "blob",
        ...config,
      });
      const blob = response.data as Blob;
      const filename =
        this.parseFilenameFromContentDisposition(
          response.headers["content-disposition"]
        ) ?? "download.pdf";
      return { blob, filename };
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        const contentType = error.response?.headers?.["content-type"] ?? "";
        if (contentType.includes("application/json")) {
          const text = await (error.response.data as Blob).text();
          try {
            const body = JSON.parse(text);
            const message =
              (Array.isArray(body?.message) ? body.message[0] : body?.message) ??
              body?.error ??
              i18n.t('toasts.downloadFailed', { ns: 'common' });
            throw new HttpError(message, error.response.status, {
              data: body,
              response: error.response,
            });
          } catch (parseError) {
            if (parseError instanceof HttpError) throw parseError;
          }
        }
      }
      throw error;
    }
  }

  private parseFilenameFromContentDisposition(header: string | undefined): string | null {
    if (!header) return null;
    const match = header.match(/filename="([^"]+)"/i);
    return match ? match[1] : null;
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 